/* Stagionello USA homepage motion. Loaded as a module, so it runs once per page. */
const doc = document.documentElement;
const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
const saveData = !!(navigator.connection && navigator.connection.saveData);

doc.classList.add('stg-js');
window.__stgBooted = true;

/* Split an element's text into lines (measured), so each line can rise in turn.
   The original markup is restored after the reveal so later resizes reflow normally. */
function splitLines(el) {
  if (el.dataset.splitDone) return;
  const original = el.innerHTML;
  el.dataset.splitDone = '1';
  const words = [];
  const walk = (node) => {
    node.childNodes.forEach((child) => {
      if (child.nodeType === 3) {
        child.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) { words.push({ space: true }); return; }
          words.push({ text: part });
        });
      } else if (child.nodeName === 'BR') {
        words.push({ br: true });
      } else {
        words.push({ html: child.outerHTML });
      }
    });
  };
  walk(el);
  el.innerHTML = words.map((w) => {
    if (w.space) return ' ';
    if (w.br) return '<br data-br>';
    return '<span class="stg-w">' + (w.html || w.text.replace(/&/g, '&amp;').replace(/</g, '&lt;')) + '</span>';
  }).join('');
  const lines = [];
  let top = null;
  let current = null;
  el.querySelectorAll('.stg-w, br[data-br]').forEach((node) => {
    if (node.nodeName === 'BR') { top = null; return; }
    const t = node.offsetTop;
    if (top === null || Math.abs(t - top) > 4) { current = []; lines.push(current); top = t; }
    current.push(node.innerHTML);
  });
  el.innerHTML = lines.map((l, i) => '<span class="stg-line" style="--i:' + i + '">' + l.join(' ') + '</span>').join('');
  el.addEventListener('stg:revealed', () => {
    const d = parseInt(getComputedStyle(el).getPropertyValue('--d') || '0', 10) || 0;
    setTimeout(() => { el.innerHTML = original; }, d + lines.length * 90 + 900);
  }, { once: true });
}

function splitLetters(el) {
  if (el.dataset.lettersDone) return;
  el.dataset.lettersDone = '1';
  const text = el.textContent.trim();
  el.setAttribute('aria-label', text);
  let i = 0;
  el.innerHTML = text.split(' ').map((word) =>
    '<span aria-hidden="true" style="display:inline-block;white-space:nowrap">' +
    [...word].map((ch) => '<span class="stg-char" style="--i:' + (i++) + '">' + ch.replace(/&/g, '&amp;').replace(/</g, '&lt;') + '</span>').join('') +
    '</span>'
  ).join('<span class="stg-space" aria-hidden="true"> </span>');
}

function countUp(el) {
  const to = parseFloat(el.dataset.count);
  if (isNaN(to)) return;
  const decimals = parseInt(el.dataset.decimals || '0', 10);
  const prefix = el.dataset.prefix || '';
  const suffix = el.dataset.suffix || '';
  const from = parseFloat(el.dataset.from || '0');
  const duration = 1400;
  const start = performance.now();
  const step = (now) => {
    const t = Math.min(1, (now - start) / duration);
    const eased = 1 - Math.pow(1 - t, 3);
    el.textContent = prefix + (from + (to - from) * eased).toFixed(decimals) + suffix;
    if (t < 1) requestAnimationFrame(step);
  };
  requestAnimationFrame(step);
}

function reveal(target) {
  target.classList.add('is-in');
  target.querySelectorAll('[data-split]').forEach((el) => el.dispatchEvent(new CustomEvent('stg:revealed')));
  if (target.matches('[data-split]')) target.dispatchEvent(new CustomEvent('stg:revealed'));
  target.querySelectorAll('[data-count]').forEach((el) => {
    const d = parseInt(el.closest('[style*="--d"]')?.style.getPropertyValue('--d') || '0', 10) || 0;
    setTimeout(() => countUp(el), d + 200);
  });
}

async function initMotion(root) {
  const targets = root.querySelectorAll('[data-stg-animate]');
  if (reduce.matches) { targets.forEach((t) => t.classList.add('is-in')); return; }
  if (document.fonts && document.fonts.ready) {
    await Promise.race([document.fonts.ready, new Promise((r) => setTimeout(r, 1200))]);
  }
  root.querySelectorAll('[data-split]').forEach(splitLines);
  root.querySelectorAll('[data-letters]').forEach(splitLetters);
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      io.unobserve(entry.target);
      reveal(entry.target);
    });
  }, { threshold: 0.18, rootMargin: '0px 0px -8% 0px' });
  targets.forEach((t) => io.observe(t));
}

/* Opening film: pick a size, fade the video in over the poster once it plays,
   pause it while off screen, and keep the still for reduced motion or data saver. */
function initFilm(film) {
  const video = film.querySelector('video');
  const tagline = film.querySelector('[data-letters]');
  if (tagline && !reduce.matches) splitLetters(tagline);
  const showTagline = () => tagline && requestAnimationFrame(() => requestAnimationFrame(() => tagline.classList.add('is-in')));
  if (!video || reduce.matches || saveData) { showTagline(); return; }
  const small = window.matchMedia('(max-width: 900px)').matches;
  const src = small ? video.dataset.srcSmall : video.dataset.srcLarge;
  if (!src) { showTagline(); return; }
  video.src = src;
  let started = false;
  const onPlaying = () => {
    video.classList.add('is-playing');
    if (!started) { started = true; setTimeout(showTagline, 350); }
  };
  video.addEventListener('playing', onPlaying);
  setTimeout(() => { if (!started) { started = true; showTagline(); } }, 1600);
  const tryPlay = () => { const p = video.play(); if (p && p.catch) p.catch(() => {}); };
  new IntersectionObserver((entries) => {
    entries.forEach((e) => { if (e.isIntersecting) tryPlay(); else video.pause(); });
  }, { threshold: 0.05 }).observe(film);
}

/* Part search posts to the store search, limited to products. */
function initPartSearch(root) {
  root.querySelectorAll('form[data-stg-search]').forEach((form) => {
    form.addEventListener('submit', (e) => {
      const q = form.querySelector('input[name="q"]');
      if (!q || !q.value.trim()) e.preventDefault();
    });
  });
}

function boot(root = document) {
  initMotion(root);
  root.querySelectorAll('[data-stg-film]').forEach(initFilm);
  initPartSearch(root);
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => boot());
else boot();

/* Theme editor: re-run for a section when it is added or re-rendered. */
document.addEventListener('shopify:section:load', (e) => boot(e.target));
